import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

// @mui
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { Alert, Box, Typography } from '@mui/material';

import UsersListSvg from '../assets/svgs/usersList';
import { BlankCard } from '../components/cards/blankCard';
import pageTitle from '../utils/pageTitles.json';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';
import { jwtDecode } from 'jwt-decode';

const ValidityPeriodSchema = Yup.object().shape({
  validityPeriod: Yup.number()
    .typeError('Validity period must be a number')
    .integer('Validity period must be a whole number')
    .min(0, 'Validity period cannot be negative')
    .required('Validity period is required'),
});

// home main section container.
const MainSection = styled('div')(() => ({
  padding: '64px 20px 0px 88px',
  backgroundColor: 'rgb(240, 240, 240)',
  height: '100vh',
  overflow: 'hidden',
}));

// Show home heading and filters.
const HeadingSection = styled('div')(() => ({
  width: '100%',
  padding: '20px 0px',
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
}));

const HeadingContentSection = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '15px',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DetailsListSection = styled('div')(() => ({
  width: '100%',
  height: 'calc(100vh - 155px)',
  overflow: 'hidden',
  overflowY: 'auto',
  padding: '20px',
}));

const ImplicitValidityPeriod = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { email } = jwtDecode(localStorage.getItem('token'));

  const methods = useForm({
    resolver: yupResolver(ValidityPeriodSchema),
    defaultValues: {
      validityPeriod: '',
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      const payload = {
        validity_period: data.validityPeriod,
        actionByEmail: email,
      };
      const result = await axiosPostCall('updateValidityPeriodSetting', payload);
      renderSuccessMessage(result?.message || 'Validity period updated successfully');
      getValidityPeriod();
    } catch (error) {
      console.error('Update validity period setting', error);
      renderErrorMessage(error?.message);
    }
  };

  const getValidityPeriod = async () => {
    try {
      const result = await axiosGetCall('getValidityPeriodSetting');
      const setting = result?.validity_period_setting ?? result?.validity_setting ?? result;

      reset({
        validityPeriod: setting?.validity_period ?? setting?.validityPeriod ?? '',
      });
    } catch (error) {
      console.error('Get validity period setting', error);
      renderErrorMessage(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getValidityPeriod();
  }, []);

  return (
    <>
      <Helmet>
        <title>{pageTitle?.ImplicitValidityPeriod}</title>
      </Helmet>

      <MainSection>
        <HeadingSection>
          <HeadingContentSection>
            <Box sx={{ height: '16px', mt: '-10px' }}>
              <UsersListSvg />
            </Box>
            <Typography variant="h5" sx={{ color: 'grey.10', fontWeight: 600 }}>
              Implicit Message Validity Period Settings
            </Typography>
          </HeadingContentSection>
        </HeadingSection>

        <BlankCard
          sx={{
            height: '100%',
            width: '100%',
            padding: '0px',
            overflow: 'hidden',
            backgroundColor: 'background.paper',
          }}
        >
          <DetailsListSection className="reportListScroll">
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  mx: '6px',
                  p: '16px',
                  maxWidth: '70%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                }}
              >
                <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Implicit Message Validity Period
                </Typography>
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{
                    alignItems: 'flex-start',
                    borderRadius: 2,
                    borderColor: 'info.light',
                    bgcolor: 'rgba(2, 136, 209, 0.06)',
                    '& .MuiAlert-icon': { color: 'info.main', pt: '4px' },
                  }}
                >
                  <strong>Note:</strong>
                  <Box component="ol" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                    <li>This setting applies only to the Implicit OTP route.</li>
                    <li>Enter the validity period in seconds.</li>
                    <li>Set the validity period to <code>0</code> to disable this setting.</li>
                    <li>
                      If the SMSC/operator supports and honors <code>validity_period</code>, it will attempt delivery
                      until the configured expiry time. After expiry, the SMSC may return <code>stat:EXPIRED</code> if
                      delivery receipts are enabled and supported.
                    </li>
                    <li>
                      If the SMSC/operator does not support or honor this field, the custom validity period may not be
                      enforced and the SMSC default may apply. An invalid value may also cause the submission to be
                      rejected.
                    </li>
                  </Box>
                </Alert>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '20px',
                    flexWrap: 'wrap',
                    width: '100%',
                    marginTop: '20px',
                  }}
                >
                  <RHFTextField
                    name="validityPeriod"
                    type="number"
                    label="Validity period (seconds)"
                    placeholder="Enter time in seconds"
                    size="small"
                    inputProps={{ min: 0, step: 1 }}
                    disabled={isSubmitting}
                    sx={{ width: { xs: '100%', sm: '240px' } }}
                  />
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting || isLoading}
                    disabled={isLoading}
                    sx={{ minWidth: '100px', ml: 'auto' }}
                  >
                    Save
                  </LoadingButton>
                </Box>
              </Box>
            </FormProvider>
          </DetailsListSection>
        </BlankCard>
      </MainSection>
    </>
  );
};

export default ImplicitValidityPeriod;
